// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/pages/ConnectPlatforms.js — the "Connect platforms" hub.
// ═══════════════════════════════════════════════════════════════════════════
// Shown only while the backend reports INGESTION_ENABLED (App.js fetches /api/ingestion/enabled).
// One hub listing the operator's connectable delivery platforms for their market.
// Connecting captures a portal session server-side and vaults it; a scheduled job
// then auto-fills the Daily Log (source='scrape'). Nothing here overwrites a
// manually-entered day.
//
// While live portal capture is being wired, the backend runs in DEMO mode
// (SCRAPER_DEMO=true): "Connect" registers the platform and "Sync now" fills the
// Daily Log with deterministic sample figures so the whole pipeline is testable.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../lib/api";
import { getCurrentSession } from "../data/auth";
import StatementUpload from "../components/StatementUpload";
import { track } from "../lib/track";

const STATUS_STYLE = {
  connected:    { label: "Connected",       bg: "#e6f4ec", fg: "#0a7d4b", dot: "#12a15e" },
  pending:      { label: "Not connected",   bg: "#eef1f4", fg: "#5b6773", dot: "#9aa5b1" },
  awaiting:     { label: "Waiting for first sync", bg: "#e8f0fb", fg: "#1e528f", dot: "#3b82f6" },
  needs_reauth: { label: "Reconnect needed", bg: "#fdf0dd", fg: "#9a6b10", dot: "#d99a2b" },
  error:        { label: "Error",           bg: "#fbe6e2", fg: "#a3372a", dot: "#d0553f" },
  disabled:     { label: "Paused",          bg: "#eef1f4", fg: "#5b6773", dot: "#9aa5b1" },
};

async function apiFetch(path, opts = {}) {
  const session = await getCurrentSession();
  const token = session?.access_token;
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  let body = null;
  try { body = await res.json(); } catch (_) { /* no body */ }
  if (!res.ok) throw new Error((body && body.error) || `Request failed (${res.status})`);
  return body;
}

export default function ConnectPlatforms({ wsId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [busy, setBusy] = useState({});     // platform -> true while a call is in flight
  const [notice, setNotice] = useState(null);
  const [pair, setPair] = useState(null);   // { platform, label, token, loginUrl } — extension pairing

  const load = useCallback(async () => {
    if (!wsId) { setState({ loading: false, error: null, data: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await apiFetch(`/api/ingestion/platforms?workspaceId=${encodeURIComponent(wsId)}`);
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: e.message, data: null });
    }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (wsId) track("connect_opened", { workspace_id: wsId }); }, [wsId]);

  const withBusy = async (platform, fn) => {
    setBusy((b) => ({ ...b, [platform]: true }));
    setNotice(null);
    try { await fn(); }
    catch (e) { setNotice({ kind: "error", text: e.message }); }
    finally { setBusy((b) => ({ ...b, [platform]: false })); }
  };

  const demo = state.data?.demo;

  const connect = (p) => withBusy(p.platform, async () => {
    // Option A: generate a one-time pairing code the operator pastes into the
    // Netrev browser extension. No portal password is ever entered in Netrev.
    const res = await apiFetch(`/api/ingestion/pair`, {
      method: "POST",
      body: JSON.stringify({ workspaceId: wsId, platform: p.platform }),
    });
    setNotice(null);
    setPair({ platform: p.platform, label: p.label, token: res.token, loginUrl: res.loginUrl || p.loginUrl });
    await load();
  });

  const disconnect = (p, connectionId) => withBusy(p.platform, async () => {
    await apiFetch(`/api/ingestion/connections/${connectionId}`, { method: "DELETE" });
    setNotice({ kind: "ok", text: `${p.label} disconnected.` });
    await load();
  });

  if (!wsId) {
    return <Shell><Empty>Select a workspace to connect its delivery platforms.</Empty></Shell>;
  }
  if (state.loading) return <Shell><Empty>Loading platforms…</Empty></Shell>;
  if (state.error) {
    return <Shell><Empty tone="error">Couldn’t load platforms: {state.error}</Empty></Shell>;
  }

  const platforms = state.data?.platforms || [];
  const vaultMissing = state.data && !state.data.vaultConfigured;

  return (
    <Shell>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "#1c2320" }}>Connect platforms</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#6d7a74", maxWidth: 560 }}>
            Connect a delivery portal with the Netrev browser extension — while you’re logged into your Partner Hub it reads your figures (gross, orders, refunds, ad spend) into your Daily Log, and Netrev computes profit with your configured rates. Your portal password never leaves your browser; manual entries are never overwritten.
          </p>
        </div>
        <span style={{ fontSize: 11, fontFamily: "monospace", letterSpacing: ".08em", textTransform: "uppercase", color: "#9a6b10", background: "#f6ecd8", padding: "4px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>
          Test feature{demo ? " · demo mode" : ""}
        </span>
      </div>

      {vaultMissing && (
        <Banner kind="error">The session vault isn’t configured on this server (SESSION_VAULT_KEY missing), so connections can’t be stored yet.</Banner>
      )}
      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {pair && (
        <div style={{ marginTop: 14, border: "1px solid #cfe3d8", borderRadius: 12, background: "#f2f9f5", padding: "16px 18px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0a5f4a", marginBottom: 8 }}>Pair the Netrev Connector extension — {pair.label}</div>
          <ol style={{ margin: "0 0 12px", paddingLeft: 20, fontSize: 13, color: "#33413b", lineHeight: 1.7 }}>
            <li>Install the <b>Netrev Connector</b> browser extension (load the <code>extension/</code> folder unpacked).</li>
            <li>Click the extension icon, paste the code below, and press <b>Save</b>.</li>
            <li>Open your {pair.label} Partner Hub{pair.loginUrl ? <> (<a href={pair.loginUrl} target="_blank" rel="noreferrer" style={{ color: "#0a5f4a" }}>open</a>)</> : null} and click <b>Capture &amp; send</b>.</li>
          </ol>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <code style={{ flex: "1 1 200px", fontSize: 12, background: "#fff", border: "1px solid #cfe3d8", borderRadius: 8, padding: "9px 11px", wordBreak: "break-all" }}>{pair.token}</code>
            <button onClick={() => { try { navigator.clipboard?.writeText(pair.token); } catch (_) {} setNotice({ kind: "ok", text: "Pairing code copied." }); }}
              style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid #0d7a5f", background: "#0d7a5f", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Copy code</button>
            <button onClick={() => setPair(null)} style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #cfd8d1", background: "#fff", color: "#6d7a74", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
          </div>
          <div style={{ fontSize: 11, color: "#6d7a74", marginTop: 8 }}>Shown once. It authorises the extension to send this platform’s figures to Netrev — it is not your portal password.</div>
        </div>
      )}

      <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
        {platforms.map((p) => {
          const conn = p.connection;
          const st = STATUS_STYLE[conn?.awaitingFirstSync ? "awaiting" : (conn?.status || "pending")];
          const isBusy = !!busy[p.platform];
          return (
            <div key={p.platform} style={{ border: "1px solid #e3e8e4", borderRadius: 12, padding: "14px 16px", background: "#fff", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 180 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#1c2320" }}>{p.label}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: st.fg, background: st.bg, padding: "3px 9px", borderRadius: 999 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.dot }} />{st.label}
                  </span>
                </div>
                <div style={{ marginTop: 3, fontSize: 12, color: "#8b9a92" }}>
                  {conn?.lastSyncedAt
                    ? `Last received ${new Date(conn.lastSyncedAt).toLocaleString()}`
                    : conn?.awaitingFirstSync
                      ? "Code issued — paste it into the extension, then open this portal's Reports/Sales page and hit Capture & send."
                      : "Pair the browser extension to start syncing"}
                  {conn?.lastError ? ` · ${conn.lastError}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn primary disabled={isBusy} onClick={() => connect(p)}>
                  {isBusy ? "…" : (conn && conn.status === "connected" ? "New code" : "Connect")}
                </Btn>
                {conn && (
                  <Btn subtle disabled={isBusy} onClick={() => disconnect(p, conn.id)}>Disconnect</Btn>
                )}
              </div>
            </div>
          );
        })}
        {platforms.length === 0 && <Empty>No platforms available for this market yet.</Empty>}
      </div>

      <StatementUpload wsId={wsId} platforms={platforms} onImported={() => load()} />
    </Shell>
  );
}

// ── small presentational helpers (self-contained; no theme dependency) ──────
function Shell({ children }) {
  return <div style={{ padding: "20px 24px 28px", maxWidth: 820 }}>{children}</div>;
}
function Empty({ children, tone }) {
  return (
    <div style={{ padding: 40, textAlign: "center", color: tone === "error" ? "#a3372a" : "#8b9a92", fontSize: 13 }}>
      {children}
    </div>
  );
}
function Banner({ kind, children }) {
  const map = {
    ok:    { bg: "#e6f4ec", fg: "#0a7d4b", bd: "#bfe3cd" },
    info:  { bg: "#e8f0fb", fg: "#1e528f", bd: "#c9ddf5" },
    error: { bg: "#fbe6e2", fg: "#a3372a", bd: "#f0c8c0" },
  };
  const c = map[kind] || map.info;
  return (
    <div style={{ marginTop: 14, padding: "10px 14px", background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, borderRadius: 10, fontSize: 13, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}
function Btn({ children, onClick, disabled, primary, subtle }) {
  const base = { padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", fontFamily: "inherit", opacity: disabled ? 0.6 : 1, transition: "all .12s", whiteSpace: "nowrap" };
  const style = primary
    ? { ...base, border: "1px solid #0d7a5f", background: "#0d7a5f", color: "#fff" }
    : subtle
      ? { ...base, border: "1px solid transparent", background: "transparent", color: "#8b9a92" }
      : { ...base, border: "1px solid #cfd8d1", background: "#fff", color: "#1c2320" };
  return <button onClick={onClick} disabled={disabled} style={style}>{children}</button>;
}
